import Image from 'next/image';
import type { FC, ReactNode } from 'react';
import { defineMessages, FormattedDate, FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { Link } from '../../../../../front/src/components/Link';
import type { Author, Category, Topic } from '../../../../../front/src/types/api';
import { buildApiFileName, isOptimizableImageSrc } from '../../../../../front/src/utils/web';

// Shared pieces for the magazine "press" blocks (media mode, ticket B2_02).
// Visual reference: the hand-coded CoreVibe home HTML blocks. The accent colour is
// driven by the tenant theme (`--color-primary`) so each media tenant keeps its brand.

// graphdebate i18n source = en-US: defaultMessage must be English (fr-FR is a Loco translation).
export const pressMessages = defineMessages({
  headline: { defaultMessage: 'Top stories', id: 't7+7VG' },
  feed: { defaultMessage: 'The feed', id: 'lFBdaF' },
  rest: { defaultMessage: 'More news', id: 'x66PX4' },
  readersCircle: { defaultMessage: 'Readers’ circle', id: 'tabKzm' },
  seeAll: { defaultMessage: 'See all', id: '078WAr' },
  live: { defaultMessage: 'Live', id: 'Dn82AL' },
  by: { defaultMessage: 'By {author}', id: 'br61pm' },
  circleCta: { defaultMessage: 'Go to the readers’ circle', id: 'xT8ciE' },
});

export const getHeroSrc = (topic: Topic): string | undefined => {
  const hero = topic.hero;
  return hero?.imageUrl || (hero?.image?.id ? buildApiFileName(hero.image.id) : undefined);
};

/**
 * Image d'illustration d'une carte presse (vignette ou visuel). Rend `<Image>` next/image OPTIMISÉ
 * (resize/WebP — crucial : une image de source réemployée fait souvent plusieurs Mo et était servie
 * BRUTE dans une vignette de 92-112 px). `unoptimized` (= <img> brut) pour un host hors whitelist
 * (fallback sûr, pas de 400). Deux modes :
 *  - `fill` (défaut) : l'image REMPLIT un parent `relative` dimensionné (`object-cover` via className).
 *  - `width`/`height` fournis : image de taille fixe (vignette autonome, pas besoin de parent relatif).
 */
export const HeroImage: FC<{
  src: string;
  alt?: string;
  className?: string;
  sizes?: string;
  width?: number;
  height?: number;
}> = ({ src, alt = '', className, sizes, width, height }) => {
  const common = {
    src,
    alt,
    className,
    loading: 'lazy' as const,
    unoptimized: !isOptimizableImageSrc(src),
  };
  if (width && height) {
    return <Image {...common} width={width} height={height} sizes={sizes} />;
  }
  return <Image {...common} fill sizes={sizes ?? '(max-width: 768px) 100vw, 400px'} />;
};

export const getAuthorName = (author?: Author | null): string | undefined => {
  if (!author) return undefined;
  // Author is a User | Guest union; redactors are Users (profile.pseudo), guests carry username.
  return (author as any)?.profile?.pseudo || (author as any)?.username || undefined;
};

/** Topic page link, e.g. /t/[slug]. Renders nothing clickable when slug is missing. */
export const ArticleLink: FC<{ slug?: string | null; className?: string; children: ReactNode }> = ({
  slug,
  className,
  children,
}) => {
  if (!slug) return <span className={className}>{children}</span>;
  return (
    <Link to="topic" params={{ slug }} passHref>
      <a className={className}>{children}</a>
    </Link>
  );
};

// Lien "stretched" vers l'article : le <a> (posé sur le titre) porte un ::after en absolute
// inset-0 qui rend TOUT l'ancêtre `relative` cliquable, sans envelopper la carte dans un <a>.
// Permet de garder le tag rubrique comme lien distinct au-dessus (relative z-10). L'ancêtre
// doit être `relative` et porter `group` pour le hover du titre.
export const StretchedTitleLink: FC<{
  slug?: string | null;
  className?: string;
  children: ReactNode;
}> = ({ slug, className, children }) => {
  if (!slug) return <span className={className}>{children}</span>;
  return (
    <Link to="topic" params={{ slug }} passHref>
      <a className={twMerge('after:absolute after:inset-0 after:content-[""]', className)}>
        {children}
      </a>
    </Link>
  );
};

/** Section header: accent square + title + rule, optional "see all" / live badge on the right. */
export const SectionHead: FC<{
  title: ReactNode;
  showLive?: boolean;
  seeAllHref?: { slug?: string };
}> = ({ title, showLive }) => (
  <div className="flex items-center gap-3.5 mb-6">
    <span className="w-[11px] h-[11px] flex-none bg-primary-base" />
    <h2 className="m-0 text-[clamp(26px,3vw,38px)] leading-none font-bold tracking-tight">
      {title}
    </h2>
    {showLive && (
      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-md text-primary-base bg-primary-base/10">
        <span className="w-[7px] h-[7px] rounded-full bg-primary-base" />
        <FormattedMessage {...pressMessages.live} />
      </span>
    )}
    <span className="flex-1 h-px bg-black/10" />
  </div>
);

// Surtitre rubrique(s) d'une carte : texte simple (pas de pastille — décision Mathias 2026-07-18),
// chaque rubrique est un LIEN vers sa page (/tech…), séparé par « · ». Gris au repos, primaire
// au survol du seul texte du lien (hover propre, pas group-hover). À placer HORS du lien de la
// carte (pas de <a> imbriqué) — voir usage dans les blocs press.
export const CategoryLinks: FC<{
  categories?: (Category | null | undefined)[] | null;
  className?: string;
}> = ({ categories, className }) => {
  const cats = (categories || []).filter((c): c is Category => !!c?.title && !!c?.slug);
  if (!cats.length) return null;
  return (
    <div
      className={twMerge(
        'text-[11px] font-extrabold uppercase tracking-wider text-gray-medium',
        className,
      )}
    >
      {cats.map((c, i) => (
        <span key={c.slug}>
          {i > 0 && <span className="mx-1 text-gray-medium">·</span>}
          <Link to="category" params={{ tag: c.slug as string }}>
            <a className="transition-colors hover:text-primary-base">{c.title}</a>
          </Link>
        </span>
      ))}
    </div>
  );
};

/** Author · date · reactions meta line. Each part is optional. */
export const ArticleMeta: FC<{
  author?: Author | null;
  date?: string | Date | null;
  reactions?: number | null;
  className?: string;
}> = ({ author, date, reactions, className }) => {
  const name = getAuthorName(author);
  const parts: ReactNode[] = [];
  if (name)
    parts.push(<FormattedMessage key="a" {...pressMessages.by} values={{ author: name }} />);
  if (date)
    parts.push(
      <FormattedDate key="d" value={new Date(date)} day="2-digit" month="2-digit" year="numeric" />,
    );
  if (typeof reactions === 'number' && reactions > 0)
    parts.push(
      <span key="r" className="inline-flex items-center gap-1 text-primary-base font-bold">
        <svg viewBox="0 0 24 24" className="w-[14px] h-[14px] fill-current" aria-hidden>
          <path d="M7.3 19 4 22V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1z" />
        </svg>
        {reactions}
      </span>,
    );

  if (!parts.length) return null;
  return (
    <div className={twMerge('text-[13.5px] font-semibold text-gray-medium', className)}>
      {parts.map((part, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i}>
          {i > 0 ? ' · ' : ''}
          {part}
        </span>
      ))}
    </div>
  );
};
